import React, { useState, useEffect } from 'react';
import { Trophy, Target, TrendingUp, Users, Award, BarChart3, Flame, Activity } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import StatCard from '../components/StatCard';
import Tabs from '../../../../components/common/Tabs';
import { pingpongStatsAPI } from '../services/pingpongStatsApi';
import { formatPercentage, formatNumber, formatRecord } from '../utils/statsFormatters';
import { calculateWinPercentage } from '../utils/statsCalculations';

/**
 * TournamentStatsView - Display tournament-level statistics
 * Shows ONLY tournament-specific stats for all participants
 *
 * @param {number} tournamentId - Tournament ID
 * @param {Object} tournament - Optional tournament object (for header info)
 */
const TournamentStatsView = ({ tournamentId, tournament }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [allParticipants, setAllParticipants] = useState([]);
  const [selectedView, setSelectedView] = useState('overview'); // 'overview' | 'players'
  const [sortBy, setSortBy] = useState('rank'); // 'rank' | 'wins' | 'winRate' | 'points'

  useEffect(() => {
    if (!tournamentId) return;

    const fetchStats = async () => {
      setLoading(true);
      try {
        // Fetch real stats from API
        const data = await pingpongStatsAPI.getTournamentStats(tournamentId);
        setStats(data);

        // Use allPlayerStats from API for the All Players tab (individual player level)
        setAllParticipants(data.allPlayerStats || []);
      } catch (error) {
        console.error('Failed to fetch tournament stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [tournamentId, tournament?.name]);

  // Sort participants
  const sortedParticipants = [...allParticipants].sort((a, b) => {
    switch (sortBy) {
      case 'wins':
        return b.wins - a.wins;
      case 'winRate':
        return b.winRate - a.winRate;
      case 'points':
        return b.pointsFor - a.pointsFor;
      case 'rank':
      default:
        return a.rank - b.rank;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-8`}>
        <p className={`text-center ${theme.textMuted}`}>
          No statistics available yet. Complete some matches to see tournament stats!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {tournament && (
        <div>
          <h2 className={`text-2xl font-bold ${theme.text} mb-1`}>
            Tournament Statistics
          </h2>
          <p className={theme.textMuted}>
            {tournament.name || 'Tournament'} - All Participants Performance
          </p>
        </div>
      )}

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'players',  label: 'All Players' },
        ]}
        activeTab={selectedView}
        onTabChange={setSelectedView}
        showMobileMenu={false}
      />

      {/* Overview Tab */}
      {selectedView === 'overview' && (
        <>
          {/* Tournament Progress Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Tournament Progress"
              value={`${stats.completionPercentage}%`}
              icon={Activity}
              subtitle={`${stats.completedMatches}/${stats.totalMatches} matches`}
              color="primary"
            />
            <StatCard
              label="Participants"
              value={stats.totalParticipants}
              icon={Users}
              subtitle={`${stats.activeParticipants} active`}
              color="success"
            />
            <StatCard
              label="Games Played"
              value={stats.totalGamesPlayed}
              icon={Target}
              color="warning"
            />
            <StatCard
              label="Avg Score Margin"
              value={stats.averagePointDifferential}
              icon={BarChart3}
              subtitle="points per game"
              color="primary"
            />
          </div>

          {/* Top Performers Grid */}
          <div>
            <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
              Top Performers (Tournament)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Most Wins */}
              {stats.mostWins && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <span className={`text-sm font-medium ${theme.textMuted}`}>Most Wins</span>
                  </div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.mostWins.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>
                    {formatRecord(stats.mostWins.wins, stats.mostWins.losses)}
                  </p>
                </div>
              )}

              {/* Top Scorer */}
              {stats.topScorer && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    <span className={`text-sm font-medium ${theme.textMuted}`}>Top Scorer</span>
                  </div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.topScorer.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>
                    {formatNumber(stats.topScorer.points)} points
                  </p>
                </div>
              )}

              {/* Highest Win Rate */}
              {stats.highestWinRate && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <span className={`text-sm font-medium ${theme.textMuted}`}>Best Win Rate</span>
                  </div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.highestWinRate.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>
                    {formatPercentage(stats.highestWinRate.winRate)} ({stats.highestWinRate.record})
                  </p>
                </div>
              )}

              {/* Longest Streak */}
              {stats.longestStreak && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    <span className={`text-sm font-medium ${theme.textMuted}`}>Hottest Streak</span>
                  </div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.longestStreak.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>
                    {stats.longestStreak.streak} consecutive wins
                  </p>
                </div>
              )}

            </div>
          </div>

          {/* Top 5 Current Standings */}
          <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`text-lg font-semibold ${theme.text}`}>
                Current Top 5 by Win %
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${theme.backgroundSecondary}`}>
                  <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    <th className="px-4 py-3 text-left">Rank</th>
                    <th className="px-4 py-3 text-left">Player</th>
                    <th className="px-4 py-3 text-center">Played</th>
                    <th className="px-4 py-3 text-center">W-L</th>
                    <th className="px-4 py-3 text-center">Win Rate</th>
                    <th className="px-4 py-3 text-center">Form</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(stats.top5Standings || []).map((player) => (
                    <tr key={player.id} className={theme.text}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                          player.rank === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          player.rank === 2 ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                          player.rank === 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          theme.backgroundSecondary
                        }`}>
                          {player.rank}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{player.name}</td>
                      <td className="px-4 py-3 text-center">{player.played}</td>
                      <td className="px-4 py-3 text-center">
                        {formatRecord(player.wins, player.losses)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {formatPercentage(player.winRate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          {player.form.map((result, idx) => (
                            <div
                              key={idx}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                result === 'W'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {result}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* All Players Tab */}
      {selectedView === 'players' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${theme.text}`}>
              All Tournament Participants ({allParticipants.length})
            </h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-3 py-2 border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-sm`}
            >
              <option value="rank">Sort by Rank</option>
              <option value="wins">Sort by Wins</option>
              <option value="winRate">Sort by Win Rate</option>
              <option value="points">Sort by Points</option>
            </select>
          </div>

          <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${theme.backgroundSecondary}`}>
                  <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    <th className="px-4 py-3 text-left">Rank</th>
                    <th className="px-4 py-3 text-left">Player</th>
                    <th className="px-4 py-3 text-center">Played</th>
                    <th className="px-4 py-3 text-center">W-L</th>
                    <th className="px-4 py-3 text-center">Win %</th>
                    {sortedParticipants[0]?.gamesWon !== undefined && (
                      <th className="px-4 py-3 text-center">Games</th>
                    )}
                    {sortedParticipants[0]?.pointsFor !== undefined && (
                      <>
                        <th className="px-4 py-3 text-center">PF-PA</th>
                        <th className="px-4 py-3 text-center">Diff</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-center">Streak</th>
                    <th className="px-4 py-3 text-center">Form</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedParticipants.map((player) => (
                    <tr
                      key={player.id}
                      className={`${theme.text} hover:${theme.cardHover} transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                          player.rank === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          player.rank === 2 ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
                          player.rank === 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          theme.backgroundSecondary
                        }`}>
                          {player.rank}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{player.name}</td>
                      <td className="px-4 py-3 text-center">{player.played}</td>
                      <td className="px-4 py-3 text-center font-medium">
                        {formatRecord(player.wins, player.losses)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {formatPercentage(player.winRate)}
                        </span>
                      </td>
                      {player.gamesWon !== undefined && (
                        <td className="px-4 py-3 text-center text-sm">
                          <span className={theme.text}>
                            {player.gamesWon}-{player.gamesLost}
                          </span>
                        </td>
                      )}
                      {player.pointsFor !== undefined && (
                        <>
                          <td className="px-4 py-3 text-center text-sm">
                            {player.pointsFor}-{player.pointsAgainst}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-medium ${
                              player.differential > 0
                                ? 'text-green-600 dark:text-green-400'
                                : player.differential < 0
                                ? 'text-red-600 dark:text-red-400'
                                : theme.textMuted
                            }`}>
                              {player.differential > 0 ? '+' : ''}{player.differential}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          player.currentStreak.type === 'win'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {player.currentStreak.type === 'win' ? 'W' : 'L'}{player.currentStreak.count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          {player.form.map((result, idx) => (
                            <div
                              key={idx}
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                result === 'W'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}
                            >
                              {result}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stats Legend */}
          <div className={`mt-4 p-4 ${theme.backgroundSecondary} rounded-lg`}>
            <p className={`text-sm ${theme.textMuted}`}>
              <strong>Legend:</strong> Played = Matches Played | W-L = Match Wins-Losses | Win % = Win Percentage |
              {sortedParticipants[0]?.gamesWon !== undefined && ' Games = Games/Sets Won-Lost |'}
              {sortedParticipants[0]?.pointsFor !== undefined && ' PF-PA = Points For - Points Against | Diff = Point Differential |'}
              {' '}Streak = Current winning/losing streak | Form = Last 5 matches (most recent on right)
            </p>
            <p className={`text-xs ${theme.textMuted} mt-2`}>
              Note: These are individual player statistics. If a player participates in team matches, their individual performance is tracked across all matches they play in.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentStatsView;
